import { Box, Button, Flex, Stack } from '@chakra-ui/react'

import { CertificationStatus } from '@/queries/constants'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'

import TopicSummary from '@/components/TopicSummary'
import Tippy from '@/components/UI/Tippy'

import TutorCertificationButton from './TutorCertificationButton'

const OUTDATED_TOPIC_TOOLTIP_TEXT = 'This topic is now outdated'

export default function TutorCertificationList({
  topics,
  showCertificationButton = true,
  showTopicDomainText = false,
}: {
  topics: Query[]
  showCertificationButton?: boolean
  showTopicDomainText?: boolean
}): JSX.Element {
  const query = useQuery()
  const user = useUser()

  const me = query.one(UserQ.where({ id: user.id }))
  const certProfileStatus = me(UserQ.getCertificationProfileStatus()) ?? CertificationStatus.None

  return (
    <Stack direction="column" spacing={6}>
      {topics.map(topic => {
        const topicId = topic(TopicQ.id)
        const topicTestUrl = topic(TopicQ.ka_url)
        const topicPracticeUrl = topicTestUrl == null ? `/topic/${topicId}` : topicTestUrl
        const topicCertStatus =
          topic(TopicQ.getTopicCertificationStatus(user.id)) ?? CertificationStatus.None
        const topicActive = topic(TopicQ.active) ?? true
        const topicType = topic(TopicQ.type)
        const certificationTypes = topic(TopicQ.certification_types) ?? []

        return (
          <Stack
            borderWidth="2px"
            borderStyle="solid"
            borderColor="border.emphasized"
            width="100%"
            p="16px"
            borderRadius="8px"
            alignItems={['flex-start', 'flex-start', 'center']}
            justifyContent="space-between"
            bg="bg"
            key={topicId ?? 0}
            direction={['column', 'column', 'row']}
            spacing="16px"
            opacity={topicActive ? 1 : 0.5}
          >
            {topicActive ? (
              <Box maxWidth={436} mr="16px">
                <TopicSummary topic={topic} showDomainText={showTopicDomainText} />
              </Box>
            ) : (
              <Tippy content={OUTDATED_TOPIC_TOOLTIP_TEXT}>
                <Box maxWidth={436} mr="16px">
                  <TopicSummary topic={topic} showDomainText={showTopicDomainText} />
                </Box>
              </Tippy>
            )}
            <Box alignItems="center">
              <Flex justifyContent="flex-end">
                {topicActive && topicTestUrl != null ? (
                  <>
                    <Button
                      variant="outline"
                      as="a"
                      href={topicPracticeUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Practice
                    </Button>
                    <Box mr="12px" />
                  </>
                ) : null}
                {!showCertificationButton ? null : (
                  <TutorCertificationButton
                    width="200px"
                    topicType={topicType}
                    topicId={topicId ?? 0}
                    topicCertStatus={topicCertStatus}
                    certProfileStatus={certProfileStatus}
                    certificationTypes={certificationTypes}
                  />
                )}
              </Flex>
            </Box>
          </Stack>
        )
      })}
    </Stack>
  )
}
